/* Privacy-preserving export & restore.
 *
 * Everything in this module runs entirely on the device. No server. No upload.
 * The user can:
 *   - download all letters as PDF / TXT / Markdown
 *   - download a readable, unencrypted backup file for safekeeping
 *   - restore from a previously downloaded backup file
 *
 * The backup file format is a plain JSON document with a small header so we
 * can validate it on restore. Sensitive content inside is NOT re-encrypted at
 * export time — it's already encrypted in the vault, but exporting decrypts it
 * into the backup file so the user can read it. This is intentional: the
 * backup file is the user's own copy, under their own control. If they want
 * disk-level protection, they should store it in their own encrypted folder.
 */

import { saveExportFile } from "@/lib/exportFile";

import { listLetters } from "@/lib/lettersVault";
import { listMosaic } from "@/lib/mosaicVault";

import { loadBook, saveBook } from "@/livingBook/storage";
import { getDraftBuffer, setDraftBuffer } from "@/lib/secureVault";
const DRAFT_SURFACES = ["journal", "letters"];
const BACKUP_VERSION = 2;
const EXTRA_KEYS = ["un_streaks_v1", "un_safe_mode_v1", "un_peace_compass_v1", "un_future_funds_v1", "un_alignment_v1", "un_protect_items_v1", "un_focus_v1"];



function isoDate() {
  return new Date().toISOString().slice(0, 10);
}

/* ---------------- Letters export (3 formats) ---------------- */

export async function exportLettersAsTxt() {
  const letters = await listLetters();
  const lines = letters.map((l) => {
    const created = new Date(l.createdAt).toLocaleString();
    const arrives = new Date(l.arrivesAt).toLocaleString();
    const status = l.derivedStatus || l.status;
    return [
      `Written: ${created}`,
      `Arrives: ${arrives}`,
      `Status: ${status}`,
      "",
      l.text,
      "",
      "—",
      "",
    ].join("\n");
  });
  const body = [
    "UnAnonymous — Tomorrow-you letters",
    `Exported: ${new Date().toLocaleString()}`,
    "",
    "—",
    "",
    ...lines,
  ].join("\n");
  return saveExportFile(new Blob([body], { type: "text/plain" }), `un-letters-${isoDate()}.txt`);
}

export async function exportLettersAsMarkdown() {
  const letters = await listLetters();
  const lines = letters.map((l) => {
    const created = new Date(l.createdAt).toLocaleString();
    const arrives = new Date(l.arrivesAt).toLocaleString();
    const status = l.derivedStatus || l.status;
    return [
      `## ${created}`,
      `*Arrives: ${arrives} · Status: ${status}*`,
      "",
      l.text,
      "",
      "---",
      "",
    ].join("\n");
  });
  const body = [
    "# Tomorrow-you letters",
    "",
    `_Exported from UnAnonymous on ${new Date().toLocaleString()}_`,
    "",
    "---",
    "",
    ...lines,
  ].join("\n");
  return saveExportFile(new Blob([body], { type: "text/markdown" }), `un-letters-${isoDate()}.md`);
}

/**
 * PDF: rather than pull a heavyweight library, we generate the simplest valid
 * single-page A4 PDF by hand. For multi-page content with proper typography
 * we'd swap in jsPDF; for v1 the user gets a clean readable PDF with no
 * embedded fonts and no external dependency.
 */
export async function exportLettersAsPdf() {
  const letters = await listLetters();

  // Build flowing text content.
  const linesPerPage = 50;
  const allLines = [];
  allLines.push("UnAnonymous - Tomorrow-you letters");
  allLines.push(`Exported: ${new Date().toLocaleString()}`);
  allLines.push("");
  letters.forEach((l) => {
    allLines.push(`Written: ${new Date(l.createdAt).toLocaleString()}`);
    allLines.push(`Arrives: ${new Date(l.arrivesAt).toLocaleString()}`);
    allLines.push(`Status: ${l.derivedStatus || l.status}`);
    allLines.push("");
    // wrap long lines to ~80 chars
    (l.text || "").split("\n").forEach((para) => {
      let remaining = para;
      while (remaining.length > 80) {
        const breakAt = remaining.lastIndexOf(" ", 80);
        const cut = breakAt > 40 ? breakAt : 80;
        allLines.push(remaining.slice(0, cut));
        remaining = remaining.slice(cut).trim();
      }
      allLines.push(remaining);
    });
    allLines.push("");
    allLines.push("---");
    allLines.push("");
  });

  // Paginate.
  const pages = [];
  for (let i = 0; i < allLines.length; i += linesPerPage) {
    pages.push(allLines.slice(i, i + linesPerPage));
  }
  if (pages.length === 0) pages.push(["No letters to export."]);

  // Build a minimal PDF.
  const objects = [];
  const pageRefs = [];
  let nextId = 1;

  // 1: catalog (placeholder, filled later with pages ref)
  const catalogId = nextId++;
  // 2: pages tree (placeholder)
  const pagesTreeId = nextId++;
  // 3: font
  const fontId = nextId++;

  pages.forEach(() => {
    const pageId = nextId++;
    const contentId = nextId++;
    pageRefs.push({ pageId, contentId });
  });

  function escapePdfText(s) {
    return s
      .replace(/\\/g, "\\\\")
      .replace(/\(/g, "\\(")
      .replace(/\)/g, "\\)")
      // Strip non-ASCII so the built-in Helvetica font can render every glyph.
      // The full unicode text is still available in the .txt / .md exports.
      .replace(/[^\x20-\x7E]/g, "?");
  }

  // Build each content stream.
  pages.forEach((linesArr, idx) => {
    const { contentId } = pageRefs[idx];
    const tj = linesArr
      .map((line) => `(${escapePdfText(line)}) Tj T*`)
      .join("\n");
    const stream = `BT /F1 11 Tf 14 TL 60 780 Td\n${tj}\nET`;
    const obj = `${contentId} 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj\n`;
    objects[contentId] = obj;
  });

  // Build each page.
  pageRefs.forEach(({ pageId, contentId }) => {
    objects[pageId] =
      `${pageId} 0 obj\n<< /Type /Page /Parent ${pagesTreeId} 0 R ` +
      `/MediaBox [0 0 595 842] /Contents ${contentId} 0 R ` +
      `/Resources << /Font << /F1 ${fontId} 0 R >> >> >>\nendobj\n`;
  });

  objects[fontId] =
    `${fontId} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`;

  const kidsList = pageRefs.map((r) => `${r.pageId} 0 R`).join(" ");
  objects[pagesTreeId] =
    `${pagesTreeId} 0 obj\n<< /Type /Pages /Count ${pageRefs.length} ` +
    `/Kids [${kidsList}] >>\nendobj\n`;

  objects[catalogId] =
    `${catalogId} 0 obj\n<< /Type /Catalog /Pages ${pagesTreeId} 0 R >>\nendobj\n`;

  // Concatenate.
  let pdf = "%PDF-1.4\n";
  const offsets = [];
  for (let i = 1; i < nextId; i++) {
    offsets[i] = pdf.length;
    pdf += objects[i];
  }
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${nextId}\n`;
  pdf += "0000000000 65535 f \n";
  for (let i = 1; i < nextId; i++) {
    pdf += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${nextId} /Root ${catalogId} 0 R >>\n`;
  pdf += `startxref\n${xrefStart}\n%%EOF`;

  return saveExportFile(new Blob([pdf], { type: "application/pdf" }), `un-letters-${isoDate()}.pdf`);
}

/* ---------------- Full backup (letters + mosaic + drafts) ---------------- */

export async function exportFullBackup() {
  const [letters, mosaic] = await Promise.all([listLetters(), listMosaic()]);

  // Drafts + journal still live in localStorage.
  let drafts = [];
  let journal = [];
  try {
    drafts = JSON.parse(localStorage.getItem("un_drafts_v1") || "[]");
  } catch (_e) {
    throw new Error("Your drafts couldn't be read. No backup file was created.");
  }
  try {
    journal = JSON.parse(localStorage.getItem("un_journal_entries_v1") || "[]");
  } catch (_e) {
    throw new Error("Your journal couldn't be read. No backup file was created.");
  }
  if (!Array.isArray(drafts) || !Array.isArray(journal)) {
    throw new Error("Some writing couldn't be read. No backup file was created.");
  }

  const draftBuffers = {};
  for (const surface of DRAFT_SURFACES) {
    const buffer = await getDraftBuffer(surface, { strict: true });
    if (buffer) draftBuffers[surface] = buffer;
  }

  const payload = {
    app: "unanonymous",
    backupVersion: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    letters,
    mosaic,
    drafts,
    draftBuffers,
    journal,
    livingBook: await loadBook(),
    localFeatures: Object.fromEntries(EXTRA_KEYS.map(k => [k, localStorage.getItem(k)])),
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  return saveExportFile(blob, `un-backup-${isoDate()}.json`);
}

/* ---------------- Restore ---------------- */

import { addLetter } from "@/lib/lettersVault";
import { addMosaic } from "@/lib/mosaicVault";

export async function importBackup(fileText) {
  let parsed;
  try {
    parsed = JSON.parse(fileText);
  } catch {
    throw new Error("That file isn't a valid UnAnonymous backup.");
  }
  if (!parsed || parsed.app !== "unanonymous" || ![1, 2].includes(parsed.backupVersion)) {
    throw new Error("That file isn't a valid UnAnonymous backup.");
  }

  const summary = { letters: 0, mosaic: 0, drafts: 0, journal: 0 };

  // Never overwrite an existing in-progress composer. Older backups omit this field.
  if (parsed.draftBuffers && typeof parsed.draftBuffers === "object") {
    for (const surface of DRAFT_SURFACES) {
      const buffer = parsed.draftBuffers[surface];
      if (!buffer || typeof buffer !== "object" || Array.isArray(buffer)) continue;
      if (!Object.values(buffer).some(value => typeof value === "string" && value.trim())) continue;
      const existing = await getDraftBuffer(surface, { strict: true });
      if (existing !== null) continue;
      await setDraftBuffer(surface, buffer, { strict: true });
      summary.drafts += 1;
    }
  }

  if (Array.isArray(parsed.letters)) {
    for (const l of parsed.letters) {
      // Re-add via the vault API so encryption happens fresh and IDs are unique.
      // eslint-disable-next-line no-await-in-loop
      await addLetter({
        text: l.text || "",
        arrivesAt: l.arrivesAt || new Date().toISOString(),
      });
      summary.letters += 1;
    }
  }

  if (Array.isArray(parsed.mosaic)) {
    for (const m of parsed.mosaic) {
      // eslint-disable-next-line no-await-in-loop
      // Restores add alongside existing pieces; never reuse an exported ID.
      const { id: _exportedId, ...piece } = m;
      await addMosaic(piece);
      summary.mosaic += 1;
    }
  }

  if (Array.isArray(parsed.drafts) && parsed.drafts.length > 0) {
    try {
      const existing = JSON.parse(localStorage.getItem("un_drafts_v1") || "[]");
      const merged = [...parsed.drafts, ...existing];
      localStorage.setItem("un_drafts_v1", JSON.stringify(merged));
      summary.drafts += parsed.drafts.length;
    } catch (e) {
      throw new Error("Your drafts couldn't be restored. Existing writing was kept.");
    }
  }

  if (Array.isArray(parsed.journal) && parsed.journal.length > 0) {
    try {
      const existing = JSON.parse(localStorage.getItem("un_journal_entries_v1") || "[]");
      const merged = [...parsed.journal, ...existing];
      localStorage.setItem("un_journal_entries_v1", JSON.stringify(merged));
      summary.journal = parsed.journal.length;
    } catch (e) {
      throw new Error("Your journal couldn't be restored. Existing writing was kept.");
    }
  }

  if (parsed.localFeatures && typeof parsed.localFeatures === "object") {
    // Restore only missing local settings; existing plans always win.
    for (const k of EXTRA_KEYS) {
      const value = parsed.localFeatures[k];
      if (typeof value === "string" && localStorage.getItem(k) === null) {
        JSON.parse(value);
        localStorage.setItem(k, value);
      }
    }
  }
  if (parsed.livingBook && typeof parsed.livingBook === "object" && !Array.isArray(parsed.livingBook)) {
    const existing = await loadBook();
    const merged = { ...parsed.livingBook };
    for (const [category, entries] of Object.entries(existing)) merged[category] = { ...merged[category], ...entries };
    await saveBook(merged);
  }
  return summary;
}
